import React, { useEffect, useState } from 'react';
import useLineAuth from '../hooks/useLineAuth';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, collection, query, where, onSnapshot } from 'firebase/firestore';
import '../styles/line.css';

import { db, auth } from '../../firebase/firebase';
import { cancelReservation } from '../../firebase/callFunctions';
import ReservedCard from '../../shared/components/ReservedCard';
import ClosedCard from '../../shared/components/ClosedCard';
import LoadingCard from '../../shared/components/LoadingCard';
import usePublicQueue from '../../shared/hooks/usePublicQueue';


export default function BookMy() {
  const { error, lineUserId, profile } = useLineAuth();

  // 自分の予約
  const [resv, setResv] = useState(null);      // { no, slotId, at, reservedType, by }
  const [busyCancel, setBusyCancel] = useState(false);

  // 予約スナップショットの初回到着フラグ
  const [resvReady, setResvReady] = useState(false);

  // Auth 初期化フラグ（画面チラつき防止に使用するだけ）
  const [authReady, setAuthReady] = useState(false);
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, () => setAuthReady(true));
    return () => unsub();
  }, []);

  // userId の確定判定（undefined の間は未確定）
  const lineAuthResolved = lineUserId !== undefined;

  // ── ここを新規追加：待ち人数/開閉/自分の前人数などは usePublicQueue から取得 ──
  const queue = usePublicQueue({
    userId: lineAuthResolved ? (lineUserId || null) : null,
    userIdField: 'lineId',
    enabled: true,            // runtime は常に購読
  });

  // 自分のLINE IDで予約の有無をリアルタイム監視（“待ち状態”のみ）
  useEffect(() => {

    setResv(null);
    setResvReady(false); // 初回は未読


    if (!lineUserId) {
      return;
    }
    const q = query(
      collection(db, 'reservation_slots'),
      where('lineId', '==', lineUserId),
      where('isReserved', '==', true),
      where('canceled', '==', false),
      where('isReceptionAccepted', '==', false),
      where('canceledLineAt', '==', null),
    );
    const unsub = onSnapshot(
      q, 
      { includeMetadataChanges: true }, 
      (snap) => {
      const fromCache = snap.metadata.fromCache === true;
      if (snap.empty) {
        // ローカルキャッシュの空はまだ判定しない（サーバー応答を待つ）
        if (fromCache) return;
        setResv(null);
        setResvReady(true);
        return;
      }
      // 同一ユーザーが複数持っていた場合は最小番号を採用
      let best = null;
      snap.forEach(docSnap => {
        const d = docSnap.data();
        // if (d.hold === true) return; // hold中は除外
        if (!best || (d.reservationNumber ?? 1e9) < (best.reservationNumber ?? 1e9)) {
          best = { id: docSnap.id, ...d };
        }
      });
      if (!best) { 
        setResv(null);  
        setResvReady(true);
        return;
      }
      const atMs = best.reservedAt?.toMillis ? best.reservedAt.toMillis() : Date.now();
      setResv({
        no: best.reservationNumber,
        slotId: best.id,
        by: lineUserId,
        reservedType: best.reservedType || 'LINE',
        at: atMs,
      });
      setResvReady(true);

    }, (err) => {
      console.error('reservation_slots watch error', err);
      setResv(null);
      setResvReady(true);
    });
    return () => unsub();
  }, [lineUserId]);

  // 取消（共通Callable）
  const handleCancel = async () => {
    if (!resv) return;
    setBusyCancel(true);
    try {
      await cancelReservation({
        slotId: resv.slotId,
        reservationNumber: resv.no,
        canceledBy: "LINE",
      });
      setResv(null);
    } catch (e) {
      alert(e?.message || '予約の取消に失敗しました。');
    } finally {
      setBusyCancel(false);
    }
  };

  // 認証エラー
  if (error) {
    return (
      <div className="line-container">
        <div className="line-card">
          <h1 className="line-title">認証エラー</h1>
          <p className="line-desc">LINEからアクセスし直してください。</p>
        </div>
      </div>
    );
  }

  // ---- 初回ロード待ち（ぜんぶ揃うまで LoadingCard）----
  if (
    !authReady ||                 // Firebase Auth 未確定
    !queue.runtimeReady ||             // ランタイム未確定（waitCount / 開閉）
    (authReady && !lineUserId) || // uid まだ
    (lineUserId && !resvReady)    // 予約スナップショット未確定
  ) {
    return (
      <div className="line-container">
        <LoadingCard />
      </div>
    );
  }

  // 予約あり → ReservedCard
  if (resv) {
    return (
      <div className="line-container">
        <ReservedCard
          resv={resv}
          myAheadCount={queue.myAheadCount} 
          displayName={profile?.displayName}
          onCancel={handleCancel}
          cancelBusy={busyCancel}
          showLinks={false}
        />
      </div>
    );
  }

  // 予約なし ＆ オンライン停止 → 閉鎖カード
  if (!queue.isOnlineOpen) {
    return (
      <div className="line-container">
        <ClosedCard />
      </div>
    );
  }

  // 予約なし表示
  return (
    <div className="line-container">
      <div className="line-card line-card--stack">
        {/* <div className="line-empty-icon" aria-hidden>📭</div> */}
        <h2 className="line-title">現在のご予約はありません。</h2>
        <p className="line-desc line-desc--muted">
          オンライン受付時間内です。受付をご希望の方は受付番号予約メニューからお進みください。
        </p>

        <div className="line-wait" style={{ marginTop: 12 }}>
          <div className="line-wait-caption">現在の待ち人数</div>
          <div className="line-wait-count">
            {queue.waitCount == null ? '—' : `${queue.waitCount}人`}
          </div>
        </div>
      </div>
    </div>
  );
  
}
